import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Calendar, MapPin, Users, Clock, Sparkles, Heart, Leaf, Sun, Moon, Wind } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SEOHead, { organizationSchema } from "@/components/SEOHead";
import LocalizedLink from "@/components/LocalizedLink";
import FAQSection from "@/components/FAQSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StagesPage = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const location = useLocation();
  const [stages, setStages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  // Get language prefix from current URL
  const getLocalizedPath = (path) => {
    const langMatch = location.pathname.match(/^\/(fr|en|ar|nl|es)/);
    const currentLang = langMatch ? langMatch[1] : lang || 'fr';
    return `/${currentLang}${path}`;
  };

  useEffect(() => {
    fetchCategories();
    fetchStages();
  }, [filter]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/stages/categories/list`);
      setCategories(res.data);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const fetchStages = async () => {
    try {
      setLoading(true);
      const params = filter !== "all" ? { category: filter } : {};
      const res = await axios.get(`${API}/stages`, { params });
      setStages(res.data);
    } catch (err) {
      console.error("Error fetching stages:", err);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (catId) => {
    const cat = categories.find((c) => c.id === catId);
    return cat?.name?.[lang] || cat?.name?.fr || catId;
  };

  const getCategoryColor = (catId) => {
    const colors = {
      yoga: "bg-purple-500/10 text-purple-600 border-purple-200",
      meditation: "bg-blue-500/10 text-blue-600 border-blue-200",
      art: "bg-pink-500/10 text-pink-600 border-pink-200",
      music: "bg-amber-500/10 text-amber-600 border-amber-200",
      wellness: "bg-green-500/10 text-green-600 border-green-200",
      spiritual: "bg-indigo-500/10 text-indigo-600 border-indigo-200",
    };
    return colors[catId] || "bg-gray-500/10 text-gray-600 border-gray-200";
  };

  return (
    <div className="min-h-screen bg-background pt-24 pb-16" data-testid="stages-page">
      <SEOHead />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-primary/10 via-background to-accent/10 py-16 mb-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles className="h-8 w-8 text-primary" />
            <Badge variant="secondary" className="text-sm">
              {lang === "fr" ? "Retraites Spirituelles" : lang === "en" ? "Spiritual Retreats" : "Retiros Espirituales"}
            </Badge>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-4">
            {lang === "fr" ? "Retraites & Stages dans le Désert" : 
             lang === "en" ? "Desert Retreats & Workshops" : 
             lang === "es" ? "Retiros y Talleres en el Desierto" :
             lang === "nl" ? "Woestijn Retraites & Workshops" :
             t("stages.title")}
          </h1>
          <p className="text-muted-foreground text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            {lang === "fr" ? "Yoga, méditation, bien-être, développement personnel... Reconnectez-vous à vous-même au cœur du Sahara marocain. Retraites spirituelles dans le désert pour une transformation profonde." : 
             lang === "en" ? "Yoga, meditation, wellness, personal development... Reconnect with yourself in the heart of the Moroccan Sahara. Spiritual desert retreats for deep transformation." :
             t("stages.intro")}
          </p>
        </div>
      </div>

      {/* SEO Keywords Section */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 mb-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Heart, title: lang === "fr" ? "Retraite Spirituelle" : "Spiritual Retreat", desc: lang === "fr" ? "Reconnexion à soi, silence, méditation" : "Self-reconnection, silence, meditation" },
            { icon: Leaf, title: lang === "fr" ? "Yoga Désert Maroc" : "Desert Yoga Morocco", desc: lang === "fr" ? "Pratique au lever du soleil sur les dunes" : "Practice at sunrise on the dunes" },
            { icon: Sun, title: lang === "fr" ? "Méditation Sahara" : "Sahara Meditation", desc: lang === "fr" ? "Pleine conscience dans l'immensité" : "Mindfulness in the vastness" },
            { icon: Wind, title: lang === "fr" ? "Retraite Holistique" : "Holistic Retreat", desc: lang === "fr" ? "Breathwork, sound healing, bien-être" : "Breathwork, sound healing, wellness" }
          ].map((item, idx) => (
            <div key={idx} className="bg-card p-5 rounded-xl border">
              <item.icon className="w-8 h-8 text-primary mb-3" />
              <h3 className="font-semibold mb-1">{item.title}</h3>
              <p className="text-sm text-muted-foreground">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Category Filters */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 mb-12">
        <div className="flex flex-wrap gap-3">
          <Button
            variant={filter === "all" ? "default" : "outline"}
            onClick={() => setFilter("all")}
            className="rounded-full"
            data-testid="stages-filter-all"
          >
            Tous
          </Button>
          {categories.map((cat) => (
            <Button
              key={cat.id}
              variant={filter === cat.id ? "default" : "outline"}
              onClick={() => setFilter(cat.id)}
              className="rounded-full"
              data-testid={`stages-filter-${cat.id}`}
            >
              {cat.name?.[lang] || cat.name?.fr}
            </Button>
          ))}
        </div>
      </div>

      {/* Stages Grid */}
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : stages.length === 0 ? (
          <div className="text-center py-20 bg-muted/30 rounded-3xl">
            <Sparkles className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Aucun stage disponible</h3>
            <p className="text-muted-foreground">
              De nouveaux stages seront bientôt annoncés. Revenez nous voir !
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {stages.map((stage) => (
              <Link
                key={stage.id}
                to={getLocalizedPath(`/stages/${stage.slug}`)}
                className="group"
                data-testid={`stage-card-${stage.slug}`}
              >
                <div className="bg-card rounded-3xl overflow-hidden border border-border hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                  {/* Image */}
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={stage.image_url || "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800"}
                      alt={stage.title?.[lang] || stage.title?.fr}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute top-4 left-4">
                      <Badge className={`${getCategoryColor(stage.category)} border`}>
                        {getCategoryName(stage.category)}
                      </Badge>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="font-serif text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                      {stage.title?.[lang] || stage.title?.fr}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                      {stage.short_description?.[lang] || stage.short_description?.fr}
                    </p>

                    {/* Meta */}
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-4 w-4" />
                        <span>{stage.duration_days} {t("stages.duration")}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Users className="h-4 w-4" />
                        <span>{stage.max_participants} max</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <MapPin className="h-4 w-4" />
                        <span>{stage.location}</span>
                      </div>
                    </div>

                    {/* Organizer */}
                    <div className="flex items-center justify-between pt-4 border-t border-border">
                      <div className="text-sm">
                        <span className="text-muted-foreground">{t("stages.organizedBy")} </span>
                        <span className="font-medium">{stage.collaborator_name}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-muted-foreground">{t("stages.from")}</span>
                        <p className="font-serif text-lg font-bold text-primary">
                          {stage.price}€
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* SEO Rich Text */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="prose prose-lg">
            {lang === "fr" ? (
              <>
                <h2 className="font-serif text-2xl font-bold mb-4">Retraite Spirituelle dans le Désert Marocain</h2>
                <p className="text-muted-foreground leading-relaxed">
                  XploreSahara vous invite à vivre une <strong>retraite spirituelle</strong> unique au cœur du <strong>désert marocain</strong>. 
                  Loin du bruit du monde, le Sahara offre le cadre idéal pour une <strong>reconnexion à soi</strong>, 
                  une <strong>transformation personnelle</strong> profonde. Nos <strong>retraites yoga désert Maroc</strong> et 
                  <strong> stages méditation Sahara</strong> sont encadrés par des praticiens expérimentés.
                  <br /><br />
                  <strong>Retraite bien-être</strong>, <strong>retraite holistique</strong>, <strong>retraite silence</strong>, 
                  <strong>breathwork</strong>, <strong>sound healing</strong> : chaque stage est conçu pour vous offrir 
                  une <strong>déconnexion totale</strong> et un <strong>développement personnel</strong> authentique 
                  dans le cadre magique des dunes d'<strong>Erg Chegaga</strong>.
                </p>
              </>
            ) : lang === "en" ? (
              <>
                <h2 className="font-serif text-2xl font-bold mb-4">Spiritual Retreat in the Moroccan Desert</h2>
                <p className="text-muted-foreground leading-relaxed">
                  XploreSahara invites you to experience a unique <strong>spiritual retreat</strong> in the heart of the <strong>Moroccan desert</strong>. 
                  Far from the noise of the world, the Sahara offers the ideal setting for <strong>self-reconnection</strong>, 
                  deep <strong>personal transformation</strong>. Our <strong>desert yoga retreats Morocco</strong> and 
                  <strong> Sahara meditation workshops</strong> are led by experienced practitioners.
                  <br /><br />
                  <strong>Wellness retreat</strong>, <strong>holistic retreat</strong>, <strong>silent retreat</strong>, 
                  <strong>breathwork</strong>, <strong>sound healing</strong>: each workshop is designed to offer you 
                  <strong>total disconnection</strong> and authentic <strong>personal development</strong> 
                  in the magical setting of the <strong>Erg Chegaga</strong> dunes.
                </p>
              </>
            ) : null}
          </div>
        </div>

        {/* FAQ Section for Retreats */}
        <FAQSection 
          category="retraites"
          lang={lang}
          title={lang === "fr" ? "Questions fréquentes - Retraites Spirituelles" : "FAQ - Spiritual Retreats"}
        />
      </div>
    </div>
  );
};

export default StagesPage;
